import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart2, CheckCircle, TrendingUp, Zap, Target } from "lucide-react";
import EmotionChartWrapper from "@/components/EmotionChart";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUser } from "@/contexts/UserContext";
import { wellnessService } from "@/services/wellnessService";

const EnergyLevelsCard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [hasCheckIns, setHasCheckIns] = useState<boolean | null>(null);
  const [checkInCount, setCheckInCount] = useState(0);
  const [latestCheckIn, setLatestCheckIn] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkForCheckIns = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        // Get stats first (more reliable for count)
        const statsData = await wellnessService.getStats().catch(() => ({ totalCheckIns: 0 }));
        const totalCheckIns = statsData?.totalCheckIns || 0;
        
        setCheckInCount(totalCheckIns);
        setHasCheckIns(totalCheckIns > 0);

        // If has check-ins, get the latest one
        if (totalCheckIns > 0) {
          const checkInsData = await wellnessService.getCheckIns({
            limit: 1,
            orderBy: 'createdAt',
            order: 'desc'
          }).catch(() => []);
          
          // apiService returns data directly
          const checkIns = Array.isArray(checkInsData) 
            ? checkInsData 
            : (checkInsData?.checkIns || checkInsData?.data || []);
          
          if (checkIns.length > 0) {
            setLatestCheckIn(checkIns[0]);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error checking for check-ins:", error);
        }
        setHasCheckIns(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkForCheckIns();
  }, [user?.id]);

  // Show empty state for new users without check-ins
  if (!isLoading && hasCheckIns === false) {
    return (
      <Card className="h-full">
        <CardContent className="p-6 text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <BarChart2 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h3 className="font-semibold mb-2">Track Your Energy Levels</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Start your journey by completing your first daily check-in. Track energy, mood, and progress over time.
          </p>
          <Button onClick={() => navigate('/check-in')} size="lg" className="w-full">
            <CheckCircle className="w-4 h-4 mr-2" />
            Complete First Check-in
          </Button>
          <p className="text-xs text-muted-foreground mt-2">Takes 2-3 minutes</p>
        </CardContent>
      </Card>
    );
  }

  // Show summary view for users with 1-3 check-ins
  if (!isLoading && checkInCount > 0 && checkInCount <= 3 && latestCheckIn) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Your Energy Snapshot</CardTitle>
              <CardDescription>
                Based on your {checkInCount} check-in{checkInCount > 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Badge variant="secondary">
              {checkInCount}/7 this week
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Zap className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-blue-700">
                {typeof latestCheckIn.energy === "number" ? `${latestCheckIn.energy}%` : "--"}
              </div>
              <div className="text-xs text-muted-foreground">Energy</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Target className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-green-700">
                {typeof latestCheckIn.focus === "number" ? `${latestCheckIn.focus}%` : "--"}
              </div>
              <div className="text-xs text-muted-foreground">Focus</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="h-4 w-4 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-purple-700">
                {typeof latestCheckIn.motivation === "number"
                  ? `${latestCheckIn.motivation}%`
                  : typeof latestCheckIn.energy === "number" && typeof latestCheckIn.focus === "number"
                  ? `${Math.round((latestCheckIn.energy + latestCheckIn.focus) / 2)}%`
                  : "--"}
              </div>
              <div className="text-xs text-muted-foreground">Motivation</div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Keep checking in daily to see your patterns and trends develop!
            </p>
            <Button onClick={() => navigate('/check-in')} className="w-full">
              <CheckCircle className="w-4 h-4 mr-2" />
              Continue Daily Check-in
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show full energy chart for users with 4+ check-ins
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Your Energy Levels</CardTitle>
        <CardDescription>
          {checkInCount >= 7 ? "Last 7 days at a glance" : "Your check-in history"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <EmotionChartWrapper />
      </CardContent>
    </Card>
  );
};

export default EnergyLevelsCard;
